import React, { useMemo, useState } from 'react';
import { TextField, MenuItem, Button, Card, CardContent, Typography, Stack, Alert } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import useSMSSettings from '../settings/hooks/useSMSSettings';

const templateOptions = [
	{ value: 'bookingConfirmation', label: 'Booking Confirmation' },
	{ value: 'scheduleChange', label: 'Schedule Change' },
	{ value: 'appointmentReminder', label: 'Appointment Reminder' },
	{ value: 'custom', label: 'Custom Message' }
];

export default function TestSMSPage() {
	const { user } = useAuth();
	const companyId = user?.companyId || null;
	const { error, sendTestSMS } = useSMSSettings(companyId);

	const [phoneNumber, setPhoneNumber] = useState('');
	const [templateType, setTemplateType] = useState('custom');
	const [customMessage, setCustomMessage] = useState('This is a test message from Neat Schedule.');
	const [submitting, setSubmitting] = useState(false);
	const [result, setResult] = useState(null);

	const isCustom = useMemo(() => templateType === 'custom', [templateType]);
	const canSubmit = useMemo(() => {
		if (!companyId) return false;
		if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 10) return false;
		if (isCustom && !customMessage.trim()) return false;
		return true;
	}, [companyId, phoneNumber, isCustom, customMessage]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!canSubmit || !companyId) return;
		setSubmitting(true);
		setResult(null);
		try {
			const ok = await sendTestSMS(
				isCustom ? 'bookingConfirmation' : templateType,
				phoneNumber,
				isCustom ? customMessage : undefined
			);
			setResult(ok ? { ok: true, message: 'Test SMS sent (accepted by server).' } : { ok: false, message: 'Failed to send test SMS.' });
		} catch (err) {
			setResult({ ok: false, message: err?.message || 'Failed to send test SMS.' });
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<Card sx={{ maxWidth: 720, mx: 'auto', mt: 4 }}>
			<CardContent>
				<Typography variant="h5" gutterBottom>
					Send Test SMS
				</Typography>
				<Typography variant="body2" color="text.secondary" gutterBottom>
					Use this tool to send a test SMS using the currently configured provider.
				</Typography>

				{!companyId && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						You must be logged in and associated with a company to send SMS.
					</Alert>
				)}
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}
				{result && (
					<Alert severity={result.ok ? 'success' : 'error'} sx={{ mb: 2 }}>
						{result.message}
					</Alert>
				)}

				<form onSubmit={handleSubmit}>
					<Stack spacing={2}>
						<TextField
							label="Destination Phone Number"
							placeholder="+1 (555) 555-1234"
							value={phoneNumber}
							onChange={(e) => setPhoneNumber(e.target.value)}
							fullWidth
							required
						/>
						<TextField
							select
							label="Template"
							value={templateType}
							onChange={(e) => setTemplateType(e.target.value)}
							fullWidth
						>
							{templateOptions.map((opt) => (
								<MenuItem key={opt.value} value={opt.value}>
									{opt.label}
								</MenuItem>
							))}
						</TextField>
						{isCustom && (
							<TextField
								label="Custom Message"
								value={customMessage}
								onChange={(e) => setCustomMessage(e.target.value)}
								multiline
								minRows={3}
								fullWidth
								required
							/>
						)}
						<Button
							type="submit"
							variant="contained"
							disabled={!canSubmit || submitting}
						>
							{submitting ? 'Sending…' : 'Send Test SMS'}
						</Button>
					</Stack>
				</form>
			</CardContent>
		</Card>
	);
}



